import React, { useState, useEffect } from "react";
import {
  Server,
  Zap,
  Globe,
  Globe2,
  Users,
  Mail,
  Lock,
  ChevronLeft,
  ChevronRight,
  Activity,
  ShieldCheck,
  Check,
  ArrowRight,
  Cpu,
  HardDrive,
  Wifi,
  Sparkles,
} from "lucide-react";

export const SERVER_NODES = [
  {
    id: "webhosting",
    nodeCode: "CH-BLADE-01",
    nodeName: "NVMe Enterprise Webspace",
    categoryLabel: "Webhosting (NVMe)",
    badge: "NVMe Gen4",
    price: "Ab CHF 9.90 / Mt.",
    icon: Globe,
    color: "#00F0FF",
    glowClass: "from-cyan-500/20 via-cyan-500/5 to-transparent",
    borderClass: "border-cyan-500/40 hover:border-cyan-400",
    ledColor: "bg-cyan-400 shadow-[0_0_12px_#00f0ff]",
    hostname: "srv01-nvme.redwork.ch",
    ip: "185.192.110.14 [Zürich Tier-IV]",
    specs: {
      cpu: "AMD EPYC™ 9654 • 3.7 GHz Turbo",
      storage: "Samsung PM1733 Enterprise NVMe (7'400 MB/s)",
      ram: "DDR5 ECC 4800 MHz (CageFS Isolated)",
      network: "10 Gbit/s Redundant Swiss Uplink",
      uptime: "99.98% SLA",
      cooling: "21.4°C Hydro-Cooled",
    },
    quote: "Ultra-schneller NVMe SSD Webspace mit LiteSpeed Enterprise, cPanel und automatischen täglichen Backups.",
    featuresList: [
      "LiteSpeed Enterprise Webserver + LSCache",
      "Kostenlose Let's Encrypt Auto-SSL Zertifikate",
      "PHP 7.4, 8.1, 8.2 & 8.3 Switcher im cPanel",
      "Tägliche Backups mit 14 Tagen Vorhaltezeit",
    ],
  },
  {
    id: "wordpress",
    nodeCode: "CH-BLADE-02",
    nodeName: "WordPress Turbo Accelerator",
    categoryLabel: "WordPress Hosting",
    badge: "LSCache Boost",
    price: "Ab CHF 6.90 / Mt.",
    icon: Zap,
    color: "#FFB703",
    glowClass: "from-amber-500/20 via-amber-500/5 to-transparent",
    borderClass: "border-amber-500/40 hover:border-amber-400",
    ledColor: "bg-amber-400 shadow-[0_0_12px_#ffb703]",
    hostname: "wp-turbo02.redwork.ch",
    ip: "185.192.110.22 [Zürich Tier-IV]",
    specs: {
      cpu: "High-Clock Intel® Xeon® Gold 6430",
      storage: "Ultra-Fast NVMe mit Redis Object Cache",
      ram: "Optimierter PHP-Memory-Pool (1024 MB)",
      network: "HTTP/3 QUIC & Brotli Kompression",
      uptime: "99.99% SLA",
      cooling: "20.8°C PUE < 1.15",
    },
    quote: "Mit LiteSpeed Cache, AccelerateWP und Staging-Umgebung für kompromisslose WordPress- und WooCommerce-Performance.",
    featuresList: [
      "Vorinstalliertes LiteSpeed Cache & Redis Cache",
      "1-Klick Staging zum gefahrlosen Testen von Updates",
      "Imunify360 KI-Malware-Schutz & Web Application Firewall",
      "Automatische Sicherheits- und Core-Updates",
    ],
  },
  {
    id: "vps",
    nodeCode: "CH-BLADE-03",
    nodeName: "Dedicated KVM Cloud Hypervisor",
    categoryLabel: "Cloud VPS & VDS",
    badge: "KVM Root",
    price: "Ab CHF 19.90 / Mt.",
    icon: Server,
    color: "#3B82F6",
    glowClass: "from-blue-500/20 via-blue-500/5 to-transparent",
    borderClass: "border-blue-500/40 hover:border-blue-400",
    ledColor: "bg-blue-400 shadow-[0_0_12px_#3b82f6]",
    hostname: "kvm-hyper03.redwork.ch",
    ip: "185.192.110.35 [Zürich Tier-IV]",
    specs: {
      cpu: "Dedizierte AMD EPYC™ vCPU Cores",
      storage: "Enterprise NVMe SSD im Hardware RAID-10",
      ram: "Garantierter DDR5 ECC RAM (Non-Overcommitted)",
      network: "10 Gbit/s ungedrosselter Schweizer Uplink",
      uptime: "99.95% Hardware SLA",
      cooling: "22.1°C Redundant N+2",
    },
    quote: "Volle Root-Freiheit mit garantierter Rechenleistung, Snapshots und Schweizer Enterprise DDoS-Shield bis zu 1 Tbit/s.",
    featuresList: [
      "Voller Root / SSH Zugriff & HTML5 VNC Konsole",
      "1 Statische Schweizer IPv4 + /64 IPv6 Subnetz",
      "Kostenlose automatisierte Server-Snapshots",
      "Debian, Ubuntu, AlmaLinux, Rocky Linux oder Windows",
    ],
  },
  {
    id: "reseller",
    nodeCode: "CH-BLADE-04",
    nodeName: "Reseller Multi-Tenant Chassis",
    categoryLabel: "Reseller Hosting",
    badge: "100% White-Label",
    price: "Ab CHF 79.90 / Mt.",
    icon: Users,
    color: "#FF7A00",
    glowClass: "from-[#FF7A00]/25 via-[#E63946]/15 to-transparent",
    borderClass: "border-[#FF7A00]/60 hover:border-[#FF7A00]",
    ledColor: "bg-[#FF7A00] shadow-[0_0_14px_#ff7a00]",
    hostname: "whm-reseller04.redwork.ch",
    ip: "185.192.110.48 [Zürich Tier-IV]",
    isFeatured: true,
    specs: {
      cpu: "Multi-Core Enterprise Cluster Cores",
      storage: "250 - 500 GB Samsung Enterprise NVMe",
      ram: "CloudLinux CageFS Mandanten-Isolation",
      network: "Eigene Nameserver (ns1.ihredomain.ch)",
      uptime: "99.98% SLA",
      cooling: "21.0°C Tier-IV Zürich",
    },
    quote: "100% White-Label mit WHM, cPanel und eigener Kundenverwaltung. Verkaufen Sie Hosting unter eigener Schweizer Marke.",
    featuresList: [
      "100% White-Label mit WHM & eigenem Logo / Branding",
      "Overselling aktiviert für maximale Flexibilität",
      "CloudLinux CageFS Isolation pro Kundenkonto",
      "Automatische SSL-Zertifikate für alle Kunden",
    ],
  },
  {
    id: "mail",
    nodeCode: "CH-BLADE-05",
    nodeName: "Enterprise Secure Mail Vault",
    categoryLabel: "E-Mail Hosting",
    badge: "SpamProtection",
    price: "Ab CHF 4.90 / Mt.",
    icon: Mail,
    color: "#0EA5E9",
    glowClass: "from-sky-500/20 via-sky-500/5 to-transparent",
    borderClass: "border-sky-500/40 hover:border-sky-400",
    ledColor: "bg-sky-400 shadow-[0_0_12px_#0ea5e9]",
    hostname: "mx-cluster05.redwork.ch",
    ip: "185.192.110.59 [Zürich Tier-IV]",
    specs: {
      cpu: "Dedizierter SpamExperts Filter Cluster",
      storage: "NVMe High-Speed Mailbox Storage",
      ram: "CalDAV, CardDAV & ActiveSync Support",
      network: "DKIM, SPF & DMARC 100% Zustellbarkeit",
      uptime: "99.99% Mail Uptime",
      cooling: "20.5°C Georedundant",
    },
    quote: "Professionelle Firmen-Postfächer mit eigener Schweizer Domain, weltweitem Webmail und integriertem SpamExperts Gateway.",
    featuresList: [
      "E-Mail mit eigener Domain (z.B. vorname@firma.ch)",
      "Roundcube Webmail & Exchange ActiveSync (EAS)",
      "SpamExperts Anti-Spam & Anti-Virus Gateway",
      "Rechtssichere Schweizer E-Mail-Archivierung (optional)",
    ],
  },
  {
    id: "ssl",
    nodeCode: "CH-BLADE-06",
    nodeName: "HSM Cryptographic Security Module",
    categoryLabel: "SSL Zertifikate",
    badge: "256-Bit SSL",
    price: "Inklusive / CHF 25",
    icon: Lock,
    color: "#10B981",
    glowClass: "from-emerald-500/20 via-emerald-500/5 to-transparent",
    borderClass: "border-emerald-500/40 hover:border-emerald-400",
    ledColor: "bg-emerald-400 shadow-[0_0_12px_#10b981]",
    hostname: "hsm-sec06.redwork.ch",
    ip: "185.192.110.72 [Zürich Tier-IV]",
    specs: {
      cpu: "Dedicated FIPS 140-2 Level 3 HSM",
      storage: "Banking-Grade 256-Bit SHA-2 & ECC",
      ram: "Sectigo & Let's Encrypt Root Trust",
      network: "OCSP Stapling für schnellste Ladezeiten",
      uptime: "100% Browser Acceptance",
      cooling: "19.8°C Vault Security",
    },
    quote: "Strikte 256-Bit SSL/TLS Verschlüsselung mit offiziellen Vertrauenssiegeln und bis zu CHF 1'750'000.- Haftungsgarantie.",
    featuresList: [
      "Let's Encrypt Auto-SSL in allen Tarifen kostenlos",
      "Sectigo Commercial SSL (DV, OV und EV Green Bar)",
      "Sichert Hauptdomain und unbegrenzt Subdomains (*.domain.ch)",
      "Höchste Konversionsrate für E-Commerce & Portale",
    ],
  },
  {
    id: "domains",
    nodeCode: "CH-BLADE-07",
    nodeName: "Anycast Schweizer DNS Backbone",
    categoryLabel: "Domain-Preise",
    badge: ".ch ab CHF 15.-",
    price: "Echtzeit-Prüfung",
    icon: Globe2,
    color: "#8B5CF6",
    glowClass: "from-purple-500/20 via-purple-500/5 to-transparent",
    borderClass: "border-purple-500/40 hover:border-purple-400",
    ledColor: "bg-purple-400 shadow-[0_0_12px_#8b5cf6]",
    hostname: "ns1.redwork-dns.ch",
    ip: "185.192.110.88 [Anycast Cluster Zürich/Genf]",
    specs: {
      cpu: "High-Performance BIND / PowerDNS Anycast",
      storage: "Verteiltes NVMe Geo-Replicated DNS",
      ram: "Echtzeit DNS-Synchronisation < 1s",
      network: "0.4 ms Durchschnittliche Schweizer Latenz",
      uptime: "100% DNS Verfügbarkeits-SLA",
      cooling: "20.9°C Dual-Region",
    },
    quote: "Registrieren Sie Ihre Wunschdomain blitzschnell mit Schweizer Anycast-DNS, DNSSEC und vollständiger WHOIS-Privatsphäre.",
    featuresList: [
      ".ch Domain für nur CHF 15.00 / Jahr",
      "Kostenlose Schweizer Anycast-DNS Nameserver",
      "DNSSEC-Signierung gegen Cache-Poisoning & Hijacking",
      "Echtzeit-DNS-Editor für A, AAAA, MX, TXT & CNAME",
    ],
  },
];

export default function CinematicServerSlider({
  activeCategory,
  onSelectCategory,
  onScrollToPackages,
}) {
  const [currentIndex, setCurrentIndex] = useState(() => {
    const idx = SERVER_NODES.findIndex((n) => n.id === activeCategory);
    return idx >= 0 ? idx : 0;
  });

  const [pulseTick, setPulseTick] = useState(0);

  // Sync currentIndex when external activeCategory changes
  useEffect(() => {
    const idx = SERVER_NODES.findIndex((n) => n.id === activeCategory);
    if (idx >= 0) {
      setCurrentIndex((prev) => (prev !== idx ? idx : prev));
    }
  }, [activeCategory]);

  // Tick for blinking activity LEDs
  useEffect(() => {
    const interval = setInterval(() => {
      setPulseTick((t) => (t + 1) % 100);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  const handleNext = () => {
    const next = (currentIndex + 1) % SERVER_NODES.length;
    setCurrentIndex(next);
    if (onSelectCategory) {
      onSelectCategory(SERVER_NODES[next].id);
    }
  };

  const handlePrev = () => {
    const prev = (currentIndex - 1 + SERVER_NODES.length) % SERVER_NODES.length;
    setCurrentIndex(prev);
    if (onSelectCategory) {
      onSelectCategory(SERVER_NODES[prev].id);
    }
  };

  const handleSelectNode = (idx) => {
    setCurrentIndex(idx);
    if (onSelectCategory) {
      onSelectCategory(SERVER_NODES[idx].id);
    }
  };

  const activeNode = SERVER_NODES[currentIndex];
  const IconComp = activeNode.icon;

  return (
    <div className="relative w-full overflow-hidden select-none">
      {/* ================================================== */}
      {/* 1. TOP SELECTOR PILLS BAR (Mobile Touch Scrollable) */}
      {/* ================================================== */}
      <div className="flex items-center justify-start sm:justify-center gap-2 overflow-x-auto no-scrollbar mb-6 sm:mb-10 max-w-5xl mx-auto px-4 pb-2">
        {SERVER_NODES.map((node, idx) => {
          const NodeIcon = node.icon;
          const isSelected = idx === currentIndex;
          const isReseller = node.id === "reseller";

          return (
            <button
              key={node.id}
              type="button"
              onClick={() => handleSelectNode(idx)}
              className={`group flex items-center gap-2 px-3.5 py-2 rounded-2xl text-xs font-bold transition-all duration-300 border cursor-pointer shrink-0 whitespace-nowrap ${
                isSelected
                  ? isReseller
                    ? "bg-gradient-to-r from-[#FF7A00]/30 to-[#E63946]/30 text-white border-[#FF7A00] shadow-[0_0_20px_rgba(255,122,0,0.4)] scale-105"
                    : "bg-white/15 text-white border-white/40 shadow-[0_0_20px_rgba(255,255,255,0.2)] scale-105"
                  : isReseller
                  ? "bg-[#181216]/80 text-[#FF9E40] border-[#FF7A00]/30 hover:border-[#FF7A00]/70 hover:bg-[#201518]"
                  : "bg-[#0c101a]/80 text-slate-400 border-white/10 hover:border-white/20 hover:text-white hover:bg-[#121826]"
              }`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-lg transition-transform group-hover:scale-110 ${
                  isSelected
                    ? "bg-white/20 text-white"
                    : "bg-white/5 text-slate-400 group-hover:text-white"
                }`}
              >
                <NodeIcon size={13} />
              </span>
              <span>{node.categoryLabel}</span>
              {node.badge && (
                <span
                  className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
                    isSelected
                      ? "bg-white/20 text-white border-white/30"
                      : "bg-white/5 text-slate-400 border-white/10"
                  }`}
                >
                  {node.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ================================================== */}
      {/* 2. 3D CINEMATIC SERVER RACK CHASSIS VIEWPORT       */}
      {/* ================================================== */}
      <div className="relative max-w-6xl mx-auto px-2 sm:px-4 perspective-[1400px]">
        {/* Ambient Glow Backdrop for Active Server Blade */}
        <div
          className="absolute -inset-4 blur-3xl opacity-35 transition-all duration-700 pointer-events-none rounded-[40px]"
          style={{
            background: `radial-gradient(circle at 50% 40%, ${activeNode.color} 0%, transparent 70%)`,
          }}
        />

        {/* Server Blade Enclosure (Dark Brushed Industrial Carbon Chassis) */}
        <div className="relative rounded-[24px] sm:rounded-[32px] bg-gradient-to-b from-[#121724] via-[#0B0F19] to-[#07090F] border border-white/15 shadow-[0_30px_100px_rgba(0,0,0,0.85)] p-4 sm:p-8 lg:p-10 backdrop-blur-2xl transition-all duration-500 overflow-hidden">
          {/* Top Chassis Ventilation Mesh & Fiber Optic Light Bar */}
          <div className="flex items-center justify-between pb-4 sm:pb-6 border-b border-white/10 flex-wrap gap-2 sm:gap-4">
            {/* Left: Blade Status & Hostname HUD */}
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <div className="flex items-center gap-2 px-2.5 sm:px-3 py-1.5 rounded-full bg-black/40 border border-white/10 font-mono text-[10px] sm:text-[11px]">
                <span className={`h-2.5 w-2.5 rounded-full ${activeNode.ledColor} animate-pulse`} />
                <span className="font-bold text-white tracking-wider">{activeNode.nodeCode}</span>
                <span className="text-slate-500">•</span>
                <span className="text-slate-300 font-semibold truncate max-w-[130px] sm:max-w-none">{activeNode.hostname}</span>
              </div>

              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[11px]">
                <Activity size={13} className="animate-spin text-emerald-400" />
                <span className="font-bold">STATUS: 99.98% ONLINE</span>
              </div>
            </div>

            {/* Right: Datacenter Location & Ping HUD */}
            <div className="flex items-center gap-2 sm:gap-3 font-mono text-[10px] sm:text-xs text-slate-300">
              <span className="px-2.5 sm:px-3 py-1.5 rounded-full bg-black/40 border border-white/10 flex items-center gap-1.5">
                <span className="text-xs sm:text-sm">🇨🇭</span>
                <span className="text-slate-200 font-bold truncate max-w-[140px] sm:max-w-none">{activeNode.ip}</span>
              </span>

              {/* Cluster Status Indicator */}
              <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                <span className="text-[9px] sm:text-[10px] font-bold font-mono tracking-wider text-emerald-400">AKTIV</span>
              </div>
            </div>
          </div>

          {/* Main Blade Core Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-8 items-center">
            {/* Left Column: Visual Server Blade Hardware Model (5 Cols) */}
            <div className="lg:col-span-5 flex flex-col justify-center">
              {/* 3D Server Blade Bay Simulation */}
              <div className="relative rounded-2xl bg-gradient-to-br from-[#1A2234] to-[#0A0D15] p-5 border border-white/15 shadow-2xl overflow-hidden">
                {/* Visual Front Panel Bezel */}
                <div className="flex items-center justify-between pb-3 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 border border-white/15 text-white shadow-inner">
                      <IconComp size={22} style={{ color: activeNode.color }} />
                    </div>
                    <div>
                      <div className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest">
                        BLADE CHASSIS
                      </div>
                      <div className="text-sm font-black text-white">{activeNode.nodeName}</div>
                    </div>
                  </div>

                  <span
                    className="text-[10px] font-mono font-black uppercase px-2.5 py-1 rounded-full border tracking-wider"
                    style={{
                      borderColor: activeNode.color,
                      color: activeNode.color,
                      backgroundColor: `${activeNode.color}15`,
                    }}
                  >
                    {activeNode.badge}
                  </span>
                </div>

                {/* 4 Hot-Swap NVMe Drive Bays with Simulated Activity LEDs */}
                <div className="my-4 space-y-2">
                  <div className="text-[10px] font-mono uppercase text-slate-400 font-bold tracking-wider flex items-center justify-between">
                    <span>HOT-SWAP NVME ARRAYS (RAID-10)</span>
                    <span className="text-emerald-400">AKTIV / SYNCHRON</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {[0, 1, 2, 3].map((bay) => {
                      const isBlinking = (pulseTick + bay) % 3 === 0;
                      return (
                        <div
                          key={bay}
                          className="flex items-center justify-between p-2 rounded-lg bg-black/60 border border-white/10 font-mono text-[10px]"
                        >
                          <div className="flex items-center gap-1.5">
                            <HardDrive size={13} className="text-slate-400" />
                            <span className="text-slate-300 font-bold">NVMe 0{bay + 1}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span
                              className={`h-1.5 w-1.5 rounded-full transition-colors ${
                                isBlinking ? "bg-emerald-400 shadow-[0_0_6px_#10B981]" : "bg-emerald-800"
                              }`}
                            />
                            <span className="text-[9px] text-slate-400">I/O</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Telemetry Readout Gauges */}
                <div className="rounded-xl bg-black/40 p-3.5 border border-white/5 space-y-2.5 font-mono text-xs">
                  <div>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-slate-400">Prozessor-Auslastung (AMD EPYC)</span>
                      <span className="text-emerald-400 font-bold">24.2%</span>
                    </div>
                    <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all duration-500"
                        style={{ width: "24.2%" }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-slate-400">NVMe I/O Durchsatz</span>
                      <span className="text-cyan-400 font-bold">7'420 MB/s</span>
                    </div>
                    <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-500"
                        style={{ width: "88%" }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1 border-t border-white/5 text-[10px] text-slate-400">
                    <span className="flex items-center gap-1">
                      <Wifi size={11} className="text-cyan-400" />
                      10 Gbit/s Uplink
                    </span>
                    <span className="text-slate-300">Ping: 0.4 ms (Zürich)</span>
                    <span className="text-emerald-400 font-bold">Temp: 21.4°C</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Category Details, Specs, Features & CTA (7 Cols) */}
            <div className="lg:col-span-7 flex flex-col justify-between space-y-6">
              <div>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span
                    className="text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full border"
                    style={{
                      borderColor: activeNode.color,
                      color: activeNode.color,
                      backgroundColor: `${activeNode.color}15`,
                    }}
                  >
                    🇨🇭 {activeNode.categoryLabel}
                  </span>

                  {activeNode.isFeatured && (
                    <span className="text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#E63946] text-white shadow-md">
                      WHM & CPANEL PARTNER
                    </span>
                  )}

                  <span className="text-sm font-mono font-bold text-amber-300 ml-auto">
                    {activeNode.price}
                  </span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight mt-1">
                  {activeNode.nodeName}
                </h3>

                <p className="text-sm sm:text-base text-slate-300 mt-3 leading-relaxed">
                  {activeNode.quote}
                </p>
              </div>

              {/* Hardware Specs Telemetry Strip - Clean Wrap without Truncate */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5 p-3 sm:p-3.5 rounded-2xl bg-white/[0.03] border border-white/10 font-mono text-xs">
                <div>
                  <span className="text-[10px] uppercase text-slate-400 block mb-0.5">Prozessor</span>
                  <span className="text-white font-bold block text-[11px] sm:text-xs leading-snug">{activeNode.specs.cpu}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase text-slate-400 block mb-0.5">NVMe Speicher</span>
                  <span className="text-white font-bold block text-[11px] sm:text-xs leading-snug">{activeNode.specs.storage}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase text-slate-400 block mb-0.5">Arbeitsspeicher</span>
                  <span className="text-white font-bold block text-[11px] sm:text-xs leading-snug">{activeNode.specs.ram}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase text-slate-400 block mb-0.5">Netzwerk Uplink</span>
                  <span className="text-white font-bold block text-[11px] sm:text-xs leading-snug">{activeNode.specs.network}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase text-slate-400 block mb-0.5">Verfügbarkeit SLA</span>
                  <span className="text-emerald-400 font-bold block text-[11px] sm:text-xs leading-snug">{activeNode.specs.uptime}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase text-slate-400 block mb-0.5">Server-Kühlung</span>
                  <span className="text-white font-bold block text-[11px] sm:text-xs leading-snug">{activeNode.specs.cooling}</span>
                </div>
              </div>

              {/* Key Bullet Features - Full Text Visible */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-200">
                {activeNode.featuresList.map((feat, fIdx) => (
                  <div key={fIdx} className="flex items-start gap-2">
                    <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 mt-0.5">
                      <Check size={11} className="font-black" />
                    </div>
                    <span className="leading-snug">{feat}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button to smooth scroll directly to pricing cards */}
              <div className="flex items-center gap-3 sm:gap-4 pt-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    if (onScrollToPackages) {
                      onScrollToPackages();
                    } else {
                      const el = document.getElementById("produkte");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                  className="w-full sm:w-auto flex items-center justify-center gap-2.5 px-5 sm:px-6 py-3 sm:py-3.5 rounded-2xl bg-gradient-to-r from-[#E63946] to-[#d62839] hover:from-[#d62839] hover:to-[#c1121f] text-white font-black text-xs sm:text-sm shadow-[0_10px_30px_rgba(230,57,70,0.4)] transition-all duration-300 hover:scale-[1.02] cursor-pointer text-center"
                >
                  <span>Passende Tarife & Pakete konfigurieren</span>
                  <ArrowRight size={16} />
                </button>

                <div className="flex items-center gap-2 text-xs text-slate-400 font-mono mx-auto sm:mx-0">
                  <ShieldCheck size={16} className="text-emerald-400" />
                  <span>30 Tage Geld-zurück-Garantie</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cinematic Arrow Navigation Controls - Positioned Gracefully */}
        <button
          type="button"
          onClick={handlePrev}
          aria-label="Vorheriger Server"
          className="absolute -left-2 sm:-left-4 top-1/2 -translate-y-1/2 flex h-9 w-9 sm:h-12 sm:w-12 items-center justify-center rounded-xl sm:rounded-2xl bg-black/80 hover:bg-black text-white border border-white/20 shadow-2xl backdrop-blur-md transition-all duration-200 hover:scale-110 cursor-pointer z-30"
        >
          <ChevronLeft size={18} className="sm:hidden" />
          <ChevronLeft size={22} className="hidden sm:block" />
        </button>

        <button
          type="button"
          onClick={handleNext}
          aria-label="Nächster Server"
          className="absolute -right-2 sm:-right-4 top-1/2 -translate-y-1/2 flex h-9 w-9 sm:h-12 sm:w-12 items-center justify-center rounded-xl sm:rounded-2xl bg-black/80 hover:bg-black text-white border border-white/20 shadow-2xl backdrop-blur-md transition-all duration-200 hover:scale-110 cursor-pointer z-30"
        >
          <ChevronRight size={18} className="sm:hidden" />
          <ChevronRight size={22} className="hidden sm:block" />
        </button>
      </div>

      {/* ================================================== */}
      {/* 3. PAGINATION DOTS & PROGRESS BAR                  */}
      {/* ================================================== */}
      <div className="flex items-center justify-center gap-3 mt-8">
        {SERVER_NODES.map((node, idx) => (
          <button
            key={node.id}
            type="button"
            onClick={() => handleSelectNode(idx)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              idx === currentIndex
                ? "h-2.5 w-8 bg-white shadow-[0_0_10px_rgba(255,255,255,0.6)]"
                : "h-2.5 w-2.5 bg-white/20 hover:bg-white/40"
            }`}
            title={node.categoryLabel}
          />
        ))}
      </div>
    </div>
  );
}
